import { useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, List } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { useDealStore } from '@/store/dealStore';
import { formatCurrency } from '@/lib/utils';

export function PipelinePage() {
  const navigate = useNavigate();
  const { deals, stages, isLoading, fetchDeals, fetchStages, updateDealStage } = useDealStore();

  useEffect(() => {
    fetchStages();
    fetchDeals();
  }, [fetchStages, fetchDeals]);

  const openDeals = deals.filter(d => d.status === 'open');

  const handleDragStart = (e: React.DragEvent, dealId: string) => {
    e.dataTransfer.setData('dealId', dealId);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
  };

  const handleDrop = async (e: React.DragEvent, stageName: string) => {
    e.preventDefault();
    const dealId = e.dataTransfer.getData('dealId');
    if (dealId) {
      await updateDealStage(dealId, stageName);
    }
  };

  if (isLoading) {
    return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div></div>;
  }

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate('/deals')}>
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold">Pipeline</h1>
            <p className="text-muted-foreground">Drag and drop deals between stages</p>
          </div>
        </div>
        <Button variant="outline" asChild>
          <Link to="/deals">
            <List className="h-4 w-4 mr-2" />
            List View
          </Link>
        </Button>
      </div>

      <div className="flex gap-4 overflow-x-auto pb-4">
        {stages.filter(s => !s.is_won && !s.is_lost).map((stage) => {
          const stageDeals = openDeals.filter(d => d.stage === stage.name);
          const stageValue = stageDeals.reduce((sum, d) => sum + d.value, 0);

          return (
            <div
              key={stage.id}
              className="flex-shrink-0 w-72"
              onDragOver={handleDragOver}
              onDrop={(e) => handleDrop(e, stage.name)}
            >
              <div className="rounded-t-lg p-3" style={{ backgroundColor: stage.color + '20', borderTop: `3px solid ${stage.color}` }}>
                <div className="flex items-center justify-between">
                  <h3 className="font-semibold" style={{ color: stage.color }}>{stage.name}</h3>
                  <span className="text-sm text-muted-foreground">{stageDeals.length}</span>
                </div>
                <p className="text-sm text-muted-foreground">{formatCurrency(stageValue)}</p>
              </div>
              <div className="bg-muted/30 rounded-b-lg p-2 min-h-[400px] space-y-2">
                {stageDeals.map((deal) => (
                  <Card
                    key={deal.id}
                    draggable
                    onDragStart={(e) => handleDragStart(e, deal.id)}
                    className="cursor-move hover:shadow-md transition-shadow"
                    onClick={() => navigate(`/deals/${deal.id}`)}
                  >
                    <CardContent className="p-3">
                      <p className="font-medium text-sm">{deal.title}</p>
                      <p className="text-xs text-muted-foreground">{deal.company_name}</p>
                      <p className="text-sm font-semibold mt-2">{formatCurrency(deal.value)}</p>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
